import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const BandhkamParvana150sqmForm = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'Bandhkam_Parvana_150sqm.pdf' : 'Building_Permission_150sqm.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm;
            }
            body {
              background-color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div className="w-full overflow-x-auto flex justify-center">
        <div ref={componentRef} className="bg-white text-black shadow-lg print:shadow-none relative" style={{ width: '210mm' }}>
          
          {/* === PAGE 1 === */}
          <div className="px-10 py-12 mx-auto relative box-border flex flex-col" style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}>
            
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold mb-2">
                {language === 'marathi' ? '- बांधकाम परवाना -' : '- Building Permission -'}
              </h1>
              <p className="text-[13px] leading-relaxed max-w-2xl mx-auto px-4">
                {language === 'marathi' 
                  ? '( महाराष्ट्र ग्रामपंचायत अधिनियम १९५८ चे कलम ५२ आणि एकत्रीकृत विकास नियंत्रण व प्रोत्साहन नियमावली चे APPENDIX"D-1" व महाराष्ट्र प्रादेशिक नियोजन व नगररचना अधिनियम , १९६६ चे कलम १८/४४/ नुसार )'
                  : '( Under Section 52 of Maharashtra Gram Panchayat Act 1958 and APPENDIX "D-1" of Unified Development Control and Promotion Regulations and Section 18/44 of Maharashtra Regional and Town Planning Act, 1966 )'}
              </p>
            </div>

            <div className="flex items-end text-[14.5px] mb-6">
               <span className="font-bold mr-2 whitespace-nowrap">{language === 'marathi' ? 'अर्जदाराचे नाव :-' : 'Applicant Name :-'}</span>
               <span className="border-b border-dashed border-black flex-grow"></span>
            </div>

            <div className="text-[14.5px] leading-[2.1] text-justify space-y-4">
              <p className="indent-12">
                {language === 'marathi' ? (
                  <>
                    ज्याअर्थी, अर्जदार श्री./ श्रीमती <span className="inline-block border-b border-dashed border-black w-80"></span> यांनी, मौजे <span className="inline-block border-b border-dashed border-black w-48"></span> ता. <span className="inline-block border-b border-dashed border-black w-48"></span> जि. <span className="inline-block border-b border-dashed border-black w-48"></span> या ग्रामपंचायतीच्या गावठाण क्षेत्रामध्ये मिळकत क्रमांक/भूमापन क्रमांक <span className="inline-block border-b border-dashed border-black w-40"></span> यामध्ये <span className="inline-block border-b border-dashed border-black w-24"></span> चौरस मीटर इमारत बांधकाम / दुरुस्ती करण्यासाठी दिनांक ----/-----/२०<span className="inline-block border-b border-dashed border-black w-10"></span> रोजी ग्रामपंचायतीचा बांधकाम परवाना मिळणेसाठी UDCPR चे परिशिष्ट (APPENDIX"k-1") मध्ये मागणी अर्ज दिला आहे.
                  </>
                ) : (
                  <>
                    Whereas, the applicant Mr./Mrs. <span className="inline-block border-b border-dashed border-black w-80"></span> has submitted an application on ----/-----/20<span className="inline-block border-b border-dashed border-black w-10"></span> in APPENDIX "K-1" of UDCPR for building permission / repair of <span className="inline-block border-b border-dashed border-black w-24"></span> square meters in property no./survey no. <span className="inline-block border-b border-dashed border-black w-40"></span> in the village area of Gram Panchayat <span className="inline-block border-b border-dashed border-black w-48"></span> Taluka <span className="inline-block border-b border-dashed border-black w-48"></span> District <span className="inline-block border-b border-dashed border-black w-48"></span>.
                  </>
                )}
              </p>

              <p className="indent-12">
                {language === 'marathi' 
                  ? 'ज्याअर्थी, अर्जदार यांनी परिशिष्ट (APPENDIX"k-1") मधील अर्जासोबत अर्जदार हे प्रस्तावित बांधकामाच्या जागेचे निर्विवाद मालक असल्याबाबत मालकी हक्क शाबित करणारे अभिलेख, बांधकामाचा मंजूर लेआउट (Plan Layout), बिल्डिंग प्लॅन (p- Line सहित), विकास शुल्क भरल्याची पोच/पावती, कामगार उपकर भरल्याची पोच/ पावती आणि नोंदणीकृत आर्किटेक्ट/लायसन्सधारक इंजिनिअर यांचे विहित नमुन्यातील प्रमाणपत्र जोडले आहे.'
                  : 'Whereas, the applicant has attached proof of undisputed ownership of the proposed building site, approved plan layout, building plan (including p-line), development fee receipt, labor cess receipt and prescribed certificate from a registered architect/licensed engineer along with the application in APPENDIX "K-1".'}
              </p>

              <p className="indent-12">
                {language === 'marathi'
                  ? 'ज्याअर्थी, प्रस्तावित बांधकाम हे १५० चौरस मीटर क्षेत्रफळापर्यंतच्या भूखंडावरील आहे.'
                  : 'Whereas, the proposed construction is on a plot area up to 150 square meters.'}
              </p>

              <p className="indent-12">
                {language === 'marathi'
                  ? 'ज्याअर्थी (लागू असेल तेथे) इमारतीचे बांधकाम करावयाची जागा/ भूखंड राष्ट्रीय महामार्ग, राज्य मार्ग तथा जिल्हा मार्ग यांच्या लगत असल्याने सदर ठिकाणी बांधकाम करण्यासाठी सक्षम रस्ते प्राधिकरणाचे ना हरकत प्रमाणपत्र अर्जासोबत जोडले आहे.'
                  : 'Whereas (where applicable), as the building site/plot is adjacent to a National Highway, State Highway or District Road, the No Objection Certificate of the competent Road Authority for construction at the said location is attached.'}
              </p>

              <p className="indent-12">
                {language === 'marathi'
                  ? 'ज्याअर्थी (लागू असेल तेथे) इमारतीचे बांधकाम धार्मिक प्रयोजनार्थ असल्याने पोलीस विभागाचे ना हरकत प्रमाणपत्र अर्जासोबत जोडले आहे.'
                  : 'Whereas (where applicable), as the building construction is for religious purposes, the No Objection Certificate of the Police Department is attached.'}
              </p>

              <p className="indent-12">
                {language === 'marathi'
                  ? 'ज्याअर्थी महाराष्ट्र ग्रामपंचायत अधिनियम १९५८ चे कलम ५२ मधील तरतुदीनुसार गावाच्या गावठाण क्षेत्रामध्ये बांधकामांना परवानगी देण्याचे अधिकार ग्रामपंचायतीला आहे.'
                  : 'Whereas, as per the provisions of Section 52 of the Maharashtra Gram Panchayat Act 1958, the Gram Panchayat has the authority to grant building permissions within the village area.'}
              </p>

              <p className="indent-12">
                {language === 'marathi'
                  ? 'त्याअर्थी, आवश्यक असणारे सर्व अभिलेख मागणी अर्जासोबत जोडले असल्याची व आवश्यक त्या सर्व सक्षम प्राधिका-याचे ना-हरकत प्रमाणपत्र अर्जदार यांनी मिळविले असल्याची खात्री झाल्याने मासिक सभेच्या मान्यतेच्या अधीन राहून अर्जदार यांना त्यांनी वर नमूद केलेप्रमाणे प्रस्तावित केलेल्या जागेमध्ये बांधकाम करण्यासाठी खालील अटी व शर्तींसह परवानगी देण्यात येत आहे.'
                  : 'Therefore, being satisfied that all necessary records are attached with the application and necessary NOCs from competent authorities have been obtained by the applicant, permission is granted to the applicant subject to the approval of the monthly meeting and the following terms and conditions for construction on the proposed site.'}
              </p>
            </div>

            <div className="mt-6 mb-2">
               <h2 className="text-[15px] font-bold">
                 {language === 'marathi' ? '➢ अटी व शर्ती :-' : '➢ Terms and Conditions :-'}
               </h2>
            </div>

            <div className="pl-2 text-[14.5px] space-y-3 leading-relaxed text-justify">
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '१)' : '1)'}</span>
                  <p>{language === 'marathi' 
                    ? 'एकत्रीकृत विकास नियंत्रण व प्रोत्साहन नियमावली ( unified development control and pramotion regulations - UDCPR) मधील तरतुदी चे काटेकोर पालन करावे . यामध्ये काही कसूर झाल्यास त्याची सर्वस्वी जबाबदारी अर्जदार यांची राहील.'
                    : 'Strict compliance with the provisions of Unified Development Control and Promotion Regulations (UDCPR) must be ensured. The applicant will be solely responsible for any default in this regard.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '२)' : '2)'}</span>
                  <p>{language === 'marathi'
                    ? 'पूर्व परवानगीची अट लागू नाही अशा आवश्यक या इतर खात्यांची परवानगी अर्जदार यांनी स्वतः घेण्याची कार्यवाही करूनच बांधकामास सुरवात करायची आहे.'
                    : 'The applicant must take action to obtain permissions from other necessary departments where prior permission condition is not applicable, before starting the construction.'}</p>
               </div>
            </div>
            
          </div>

          <div className="html2pdf__page-break"></div>

          {/* === PAGE 2 === */}
          <div className="px-10 py-12 mx-auto relative box-border flex flex-col" style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}>
            
            <div className="pl-2 text-[14.5px] space-y-4 leading-relaxed text-justify">
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '३)' : '3)'}</span>
                  <p>{language === 'marathi'
                    ? 'प्रस्तावित बांधकाम या आदेशाच्या दिनांकापासून एक वर्षाच्या आत प्रारंभ करणे बंधनकारक आहे. या बांधकाम परवाना दिनांकापासून एक वर्ष संपल्यानंतर या परवान्यांच्या आधारे कामाला प्रारंभ करता येणार नाही. नव्याने बांधकाम परवाना मिळवावा लागेल.'
                    : 'It is mandatory to commence the proposed construction within one year from the date of this order. After one year, a new building permission will have to be obtained.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '४)' : '4)'}</span>
                  <p>{language === 'marathi'
                    ? 'परवानगीशिवाय करण्यात येणाऱ्या बांधकामासाठी अर्जदार हे महाराष्ट्र ग्रामपंचायत अधिनियम १९५८ चे कलम ५२(४) (५) नुसार कारवाईस पात्र राहतील.'
                    : 'For construction without permission, the applicant will be liable for action under Section 52(4)(5) of the Maharashtra Gram Panchayat Act 1958.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '५)' : '5)'}</span>
                  <p>{language === 'marathi'
                    ? 'अर्जदार यांनी सार्वजनिक रस्त्यावर किंवा जागेत किंवा त्यावर किंवा अशा रस्त्यातील किंवा जागेतील उघडे जलनिःसारण, गटार, मल प्रणाल किंवा सेतुप्रणाल यात किंवा यावर बांधकाम केल्यास त्यावर अतिक्रमण समजून महाराष्ट्र ग्रामपंचायत अधिनियम १९५८ चे कलम ५३ नुसार पंचायतीकडून कारवाई करण्यात येईल.'
                    : 'If the applicant constructs on a public road or space, or on open drains, sewers, or culverts in such road or space, it will be treated as an encroachment and action will be taken under Section 53 of the Maharashtra Gram Panchayat Act 1958.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '६)' : '6)'}</span>
                  <p>{language === 'marathi'
                    ? 'लोकांचे किंवा कोणत्याही व्यक्तीचे गैरसोय होईल अशा रीतीने बांधकामाचे कोणतेही साहित्य बांधकाम सुरू असताना व संपल्यानंतर देखील सार्वजनिक रस्त्यावर ठेवता येणार नाही. सार्वजनिक वाहतुकीस अडथळा होणार नाही याची दक्षता घ्यावी.'
                    : 'No construction material shall be kept on public roads during and after construction in a manner that causes inconvenience to the public. Care should be taken not to obstruct public transport.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '७)' : '7)'}</span>
                  <p>{language === 'marathi'
                    ? 'सदर ची बांधकाम परवानगी ही इमारत बांधकामाच्या चतु:सीमेतील व्यक्तीची अर्जदार यांच्या बांधकामास काहीही हरकत नाही असे गृहीत धरून देण्यात येत आहे. तथापि, यासंदर्भात भविष्यात कोणतीही हरकत ग्रामपंचायतीकडे प्राप्त झाल्यास व प्राप्त हरकत वैध असल्याचे निष्पन्न झाल्यास बांधकामाच्या कोणत्याही टप्प्यावर बांधकाम बंद करण्याचे व हरकती चे निरसन करण्याचे अधिकार पंचायतीस आहे. याची नोंद घ्यावी. जागेच्या हद्दीबाबत व अनुषंगिक काही तक्रार अगर कायदेशीर अडचण आल्यास त्याची जबाबदारी अर्जदार यांचेवर राहील.'
                    : 'This building permission is granted assuming no objection from persons within the boundaries of the construction. However, if any valid objection is received in the future, the Panchayat has the right to stop construction at any stage. The applicant is responsible for any complaints or legal issues regarding boundaries.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '८)' : '8)'}</span>
                  <p>{language === 'marathi'
                    ? 'इमारती चे बांधकाम चतु:सीमेलगतचे शेजाऱ्याचे हद्दीत बांधकामाचा कोणताही भाग येता कामा नये. मंजूर नकाशात दर्शविल्याप्रमाणे रस्त्यापासून सेटबॅक, (अंतर) काटेकोर पणे सोडणे आवश्यक आहे. कोणत्याही प्रकारचे अतिक्रमण करता कामा नये.'
                    : 'No part of the construction should encroach on the neighbor\'s boundary. Setbacks from the road as shown in the approved plan must be strictly maintained. Encroachment of any kind is prohibited.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '९)' : '9)'}</span>
                  <p>{language === 'marathi'
                    ? 'प्रस्तावित इमारत बांधकामामुळे ओढे /नाले /नदी इ चा नैसर्गिक प्रवाह खंडीत अथवा त्यामध्ये बाधा येईल किंवा ते मुरविले जातील अशा रीतीने बांधकाम करता येणार नाही. पुरेशेचे उल्लंघन होणार नाही याची दक्षता घ्यावी.'
                    : 'Construction shall not disrupt, obstruct, or divert the natural flow of streams, nullahs, or rivers. Care should be taken to prevent flooding.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '१०)' : '10)'}</span>
                  <p>{language === 'marathi'
                    ? 'UDCPR प्रमाणे अनुज्ञेय चटई क्षेत्र निर्देशांक (FSI) चे उल्लंघन होणार नाही याची जबाबदारी अर्जदार यांच्यावर राहील.'
                    : 'The applicant is responsible for ensuring no violation of the permissible Floor Space Index (FSI) as per UDCPR.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '११)' : '11)'}</span>
                  <p>{language === 'marathi'
                    ? 'UDCPR प्रमाणे इमारती च्या सभोवताली मोकळी जागा (open space) सोडणे बंधनकारक राहील.'
                    : 'Leaving open space around the building as per UDCPR is mandatory.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '१२)' : '12)'}</span>
                  <p>{language === 'marathi'
                    ? 'शासन निर्णय दिनांक ०९ मार्च २००१ व त्यानंतर वेळोवेळी होणाऱ्या सुधारणा नुसार अनुज्ञेय इमारत रेषा व नियंत्रण रेषा यांच्या नियंत्रणाचे उल्लंघन होणार नाही याची दक्षता अर्जदार यांनी घ्यावयाची आहे.'
                    : 'The applicant must ensure compliance with permissible building and control lines as per Government Resolution dated March 9, 2001, and subsequent amendments.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '१३)' : '13)'}</span>
                  <p>{language === 'marathi'
                    ? 'इमारत बांधकाम करावयाची जागा / भूखंड राष्ट्रीय महामार्ग, राज्य मार्ग तथा जिल्हा मार्ग यांच्या लगत आल्यास सक्षम रस्ते प्राधिकरणाचे नाहरकत प्रमाणपत्रातील अटी व शर्तींचे उल्लंघन होणार नाही याची दक्षता अर्जदार यांनी घेणेची आहे.'
                    : 'If the plot is adjacent to a National, State, or District Highway, the applicant must ensure compliance with the terms of the NOC from the competent road authority.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '१४)' : '14)'}</span>
                  <p>{language === 'marathi'
                    ? 'सांडपाण्याची व पावसाच्या पाण्याची विल्हेवाट लगतच्या मिळकतधारकास त्रास होणार नाही अशा रीतीने करण्यात यावी. रस्त्याचे व इतरांचे जलस्रोत बाधित झाल्यास अर्जदार स्वतः जबाबदार असतील.'
                    : 'Disposal of wastewater and rainwater should not cause a nuisance to adjacent property owners. The applicant is responsible for any damage to roads or water sources.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '१५)' : '15)'}</span>
                  <p>{language === 'marathi'
                    ? 'अर्जदार यांनी बांधकाम करतेवेळी वैयक्तिक शौचालय बांधणे बंधनकारक आहे.'
                    : 'The applicant must construct an individual toilet during construction.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '१६)' : '16)'}</span>
                  <p>{language === 'marathi'
                    ? 'घरगुती वापराचे सांडपाण्यासाठी स्वतंत्र शोषखड्डा असणे बंधनकारक आहे.'
                    : 'A separate soak pit for domestic wastewater is mandatory.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '१७)' : '17)'}</span>
                  <p>{language === 'marathi'
                    ? 'घरातील ओल्या व सुक्या ( कुजणा-या व न कुजणाऱ्या कचऱ्याचे तथा कुजणाऱ्या कचऱ्यासाठी कौटुंबिक कंपोस्ट पिटची व्यवस्था अर्जदार यांनी करणे बंधनकारक राहील.'
                    : 'The applicant must provide a domestic compost pit for wet and dry waste segregation and disposal.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '१८)' : '18)'}</span>
                  <p>{language === 'marathi'
                    ? 'अर्जदार यांच्यावर वृक्ष संवर्धन अधिनियम व त्यासंबंधातील नियम बंधनकारक राहतील. अर्जदार यांनी प्रस्तावित इमारतीच्या आवारात स्वतः चे भूखंडामध्ये भूखंडाच्या प्रत्येक १०० चौ. मी क्षेत्रात एक याप्रमाणे किमान झाडे लावून त्याचे संवर्धन/ संगोपन करणे बंधनकारक राहील.'
                    : 'The applicant is bound by the Tree Conservation Act. The applicant must plant and nurture at least one tree per 100 sq.m of plot area.'}</p>
               </div>
            </div>

          </div>

          <div className="html2pdf__page-break"></div>

          {/* === PAGE 3 === */}
          <div className="px-10 py-12 mx-auto relative box-border flex flex-col" style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}>
            
            <div className="pl-2 text-[14.5px] space-y-4 leading-relaxed text-justify mb-8">
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '१९)' : '19)'}</span>
                  <p>{language === 'marathi'
                    ? 'बांधकाम सुरू असताना मंजूर बांधकाम नकाशा/ रेखांकनाची प्रत बांधकाम स्थळी सनियंत्रणासाठी उपलब्ध ठेवावी. तसेच पंचायतीने वा पंचायतीच्या प्रतिनिधीने मागणी केल्यास ती उपलब्ध करून दिली पाहिजे.'
                    : 'A copy of the approved plan/drawing must be kept at the site during construction and provided to the Panchayat representative on demand.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '२०)' : '20)'}</span>
                  <p>{language === 'marathi'
                    ? 'प्रस्तावित बांधकामास अस्तित्वातील रस्ता उपलब्ध असणे आवश्यक आहे.'
                    : 'An existing approach road must be available for the proposed construction.'}</p>
               </div>
               <div className="flex items-start">
                  <span className="mr-2 font-bold whitespace-nowrap">{language === 'marathi' ? '२१)' : '21)'}</span>
                  <p className="leading-loose">
                     {language === 'marathi'
                       ? '(लागू असेल तेथे)'
                       : '(Where applicable)'} <span className="inline-block border-b border-dashed border-black w-48"></span> {language === 'marathi' ? 'जिल्हा भूकंपप्रवण क्षेत्रात येत असल्यामुळे भूकंपासारख्या नैसर्गिक आपत्ती मध्ये बांधकामाचे नुकसान होणार नाही.अशा रीतीने भूकंपरोधक नियमावलीस अनुसरून बांधकाम करावे.' : 'Since the district falls in an earthquake-prone zone, construction must follow earthquake-resistant guidelines.'}
                  </p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '२२)' : '22)'}</span>
                  <p>{language === 'marathi'
                    ? 'इमारतीकरिता जे बांधकाम साहित्य आणले जाईल त्याची शासनाने विहित केलेले शुल्क / परवाना फी रॉयल्टी इ. ज्या ज्या वेळी अर्जदाराने संबंधित महसूल कार्यालयात भरणा करणे बंधनकारक राहील.'
                    : 'The applicant must pay all applicable royalties and fees for construction materials to the concerned Revenue Office.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '२३)' : '23)'}</span>
                  <p>{language === 'marathi'
                    ? 'इमारत बांधकाम पुर्ण झाल्यानंतर इमारतीचा ताबा/भोगवटा घेत असल्याचे संबंधित मिळकत धारकाने ग्रामपंचायतीला लेखी कळविणे आवश्यक आहे.. बांधकाम पूर्ण झाल्यावर महाराष्ट्र कर व फी नियम १९६० मधील तरतुदीनुसार अर्जदार यांचे वर मालमत्ता कर , विजकर, आरोग्यकर , व नियमोचित अन्य कर व फी बसवण्यात येईल व असे कर व फी अर्जदार यांच्यावर बंधनकारक राहतील.'
                    : 'Upon completion, the owner must notify the Gram Panchayat in writing about taking possession/occupancy. Applicable property tax, water tax, health tax, and other fees as per rules will be levied and are binding.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2 font-bold">{language === 'marathi' ? '२४)' : '24)'}</span>
                  <p>{language === 'marathi'
                    ? 'अर्जदार यांनी सदरचे बांधकाम नोंदणीकृत आर्किटेक्ट/ लायसन्सधारक इंजिनिअर यांचे देखरेखीखाली एकत्रीकृत विकास नियंत्रण व प्रोत्साहन नियमावली (unified development control and pramotion regulations - UDCPR) मधील तरतुदींचे काटेकोर पालन करून पूर्ण करावयाचे आहे.'
                    : 'The construction must be completed under the supervision of a registered architect/licensed engineer in strict compliance with UDCPR provisions.'}</p>
               </div>
               <div className="flex flex-col mt-2">
                  <div className="flex items-end">
                     <span className="mr-2 font-bold">{language === 'marathi' ? '२५)' : '25)'}</span>
                     <span className="border-b border-dashed border-black flex-grow w-full"></span>
                  </div>
                  <div className="flex items-end mt-3">
                     <span className="border-b border-dashed border-black flex-grow w-full ml-8"></span>
                  </div>
               </div>
            </div>

            <div className="text-[14.5px] leading-[2.1] text-justify indent-12 mt-6">
              <p>
                {language === 'marathi'
                  ? 'कोणत्याही प्रकारच्या अटी व शर्तींचा भंग केल्याचे किंवा उल्लंघन केल्याचे किंवा अर्जामध्ये चुकीची माहिती दिल्याचे किंवा अर्जासोबत चुकीची/ खोटी / बनावट कागदपत्रे सादर केल्याचे निदर्शनास आल्यास सदरहू बांधकाम परवाना रद्द करण्याचे अधिकार पंचायतीस राहतील. तसेच याबाबत अर्जदार हे नियमोचित कारवाईस पात्र असतील.'
                  : 'The Panchayat reserves the right to cancel this building permission if any terms and conditions are violated, or if false/fake documents or incorrect information were submitted. The applicant will be liable for legal action in such cases.'}
              </p>
            </div>

            {/* Bottom Signatures Area */}
            <div className="flex flex-col items-center mt-32 text-[15px] space-y-8">
               <div className="flex justify-between w-3/4">
                  <p className="font-bold text-center">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'}</p>
                  <p className="font-bold text-center">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
               </div>
               <div className="flex items-end justify-center w-full">
                  <span className="mr-2">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                  <span className="border-b border-dashed border-black w-64"></span>
               </div>
            </div>

            <div className="absolute bottom-10 left-10 text-blue-700 text-sm underline print:hidden">
              www.majhigrampanchayat.com
            </div>

          </div>

        </div>
      </div>
    </div>
  );
};

export default BandhkamParvana150sqmForm;
